import { BaseAPI } from '../../../../shared/infra/api/BaseAPI';
import { APIResponse } from '../../../../shared/infra/api/APIResponse';
import { IAuthService } from '../../../users/services/authService';
import { Result } from '../../../../shared/core/Result';
import { right, left } from '../../../../shared/core/Either';
import { PublishItemRequestDTO } from '../../../../shared/components/publish/dtos/PublishItem.dto';
import { ArchiveFeedItemsRequestDTO } from '../dtos/ArchiveFeedItems.dto';
import { DeleteFeedItemsRequestDTO } from '../dtos/DeleteFeedItems.dto';
import { AxiosError } from 'axios';
import {
  ViewItemConversionsResponse,
  ViewItemNotificationResponse,
  ViewItemRequestDTO,
  ViewItemResponse,
  ViewItemFilterRequestDTO,
  ViewItemViewsResponse,
  ViewItemViewershipResponse,
  ViewItemActivityLogResponse,
  FeedItemAnalyticsData,
  ViewItemActivityRequestDTO,
  ViewItemPublishingLogResponse,
} from '../infra/types/FeedItemViews';
import {
  FeedItemModelPropsUnion,
  FeedItemViewModel,
} from '../models/FeedItemView.model';
import { FeedItemViewUtils } from '../utils/FeedItemViewUtils';
import { LogDateCountModel } from '../../analytics/models/LogDateCount.model';
import { LogDateCountUtil } from '../../analytics/utils/LogDateCountUtil';
import { FeedItemNotificationModel } from '../models/FeedItemNotification.model';
import { FeedItemNotificationUtils } from '../utils/FeedItemNotificationUtils';
import { FeedItemConversionDTO } from '../dtos/FeedItemConversions.dto';
import { FeedItemViewershipModel } from '../models/FeedItemViewership.model';
import { FeedItemViewershipUtils } from '../utils/FeedItemViewershipUtils';
import { FeedItemActivityLogModel } from '../models/FeedItemActivityLog.model';
import { PaginationDataModel } from '../../../../shared/components/pagination/model/Pagination.model';
import { PaginationUtil } from '../../../../shared/components/pagination/utils/PaginationUtils';
import { FeedItemActivityLogUtils } from '../utils/FeedItemActivityLogUtils';
import { LogDateFilterType } from '../../analytics/infra/types';
import { RetractFeedItemsRequestDTO } from '../dtos/RetractFeedItems.dto';
import { FeaturedFeedItemModel } from '../models/FeaturedFeedItem.model';
import {
  CommunityPinnedItemResponse,
  CommunityPinnedItemsResponse,
  MonthlyPublishingItemResponse,
  PinningFeedItemsResponse,
  PublicFeedItemsResponse,
  RemovePinnedStateRequest,
  SetPinnedStateRequest,
} from '../infra/types';
import { FeaturedUtil } from '../utils/FeaturedUtils';
import { FeedItemPublishingLogModel } from '../models/FeedItemPublishingLog.model';
import { FeedItemPublishingLogUtils } from '../utils/FeedItemPublishingLogUtils';
import { FeedItemModel } from '../models/FeedItem.model';
import { PublishingItemModel } from '../models/PublishingItem.model';
import { PublishingUtil } from '../utils/PublishingUtils';
import { DateQuery } from '../../publishing/infra/api/publishing.api';

export interface IFeedItemService {
  publishFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>>;
  retractFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>>;
  retractFeedItems(
    retractReq: RetractFeedItemsRequestDTO,
  ): Promise<APIResponse<void>>;
  archiveFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>>;
  destroyFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>>;
  archiveFeedItems(
    archiveItems: ArchiveFeedItemsRequestDTO,
  ): Promise<APIResponse<void>>;
  deleteFeedItems(
    deleteItems: DeleteFeedItemsRequestDTO,
  ): Promise<APIResponse<void>>;
  fetchFeedItem(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemViewModel<FeedItemModelPropsUnion>>>;
  fetchFeeditemViews(
    viewRequest: ViewItemFilterRequestDTO,
  ): Promise<APIResponse<LogDateCountModel[]>>;
  fetchFeedItemViewership(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemViewershipModel>>;
  fetchFeedItemNotifications(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemNotificationModel[]>>;
  fetchFeedItemConversions(
    viewRequest: ViewItemFilterRequestDTO,
  ): Promise<APIResponse<FeedItemConversionDTO>>;
  fetchFeedItemActivity(
    viewRequest: ViewItemActivityRequestDTO,
  ): Promise<APIResponse<PaginationDataModel<FeedItemActivityLogModel[]>>>;
  fetchFeedItemPublishingActivity(
    viewRequest: ViewItemActivityRequestDTO,
  ): Promise<APIResponse<PaginationDataModel<FeedItemPublishingLogModel[]>>>;
  fetchPublicFeedItems(
    offset?: number,
    filter?: string,
    dir?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<FeaturedFeedItemModel[]>>>;
  fetchFeedItemsToPin(
    offset?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<FeedItemModel[]>>>;
  setPinnedFeedItem(
    pinnedRequest: SetPinnedStateRequest,
  ): Promise<APIResponse<void>>;
  unsetPinnedFeedItem(
    unpinRequest: RemovePinnedStateRequest,
  ): Promise<APIResponse<void>>;
  fetchCommunityPinnedItem(): Promise<
    APIResponse<FeaturedFeedItemModel | null>
  >;
  fetchCommunityPinnedItems(): Promise<APIResponse<any>>;
  getCommunityMonthlyItems(
    dateQuery: DateQuery,
  ): Promise<APIResponse<PublishingItemModel[]>>;
}

export class FeedItemService extends BaseAPI implements IFeedItemService {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onFailureExecuteImpl(error: AxiosError<any>): Promise<any> {
    throw new Error('Method not implemented.');
  }

  constructor(authService: IAuthService) {
    super(authService);
  }

  hasToken(): boolean {
    return this.authService.isUserAuthenticated();
  }

  public async getCommunityMonthlyItems(
    dateQuery: DateQuery,
  ): Promise<APIResponse<PublishingItemModel[]>> {
    try {
      const { startDate, endDate } = dateQuery;

      const response = await this.get<MonthlyPublishingItemResponse>({
        url: '/v2/publishing/current',
        requiresToken: true,
        config: {
          params: { startDate, endDate },
        },
      });
      const { data } = response;
      const pubItems = data.data;

      return right(
        Result.ok<PublishingItemModel[]>(
          pubItems.map((item) => PublishingUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async fetchFeedItemsToPin(
    offset?: number,
    search?: string,
  ): Promise<APIResponse<PaginationDataModel<FeaturedFeedItemModel[]>>> {
    try {
      const response = await this.get<PinningFeedItemsResponse>({
        url: '/v2/feed/community/feeditems',
        requiresToken: true,
        config: {
          params: { offset, search },
        },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<FeaturedFeedItemModel[]>>({
          meta: PaginationUtil.mapToViewModel(meta),
          model: data.map((item) => FeaturedUtil.mapToViewModel(item)),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async publishFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!publishReq.id)
      return left({ message: 'Feed Item is invalid or could not be found' });
    try {
      await this.patch<void>({
        url: `/v2/feed/publish/${publishReq.id}`,
        requiresToken: true,
        payload: {
          groups: publishReq.groups,
          recipients: publishReq.recipients,
          notification: publishReq.notification,
          featured: publishReq.featured,
          open: publishReq.open,
        },
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async setPinnedFeedItem(
    pinnedRequest: SetPinnedStateRequest,
  ): Promise<APIResponse<void>> {
    if (!pinnedRequest.feedItemId) {
      return left({ message: 'Feed item is invalid or could not be found' });
    }

    try {
      await this.patch<void>({
        url: `/v1/feedItem/pinned/${pinnedRequest.feedItemId}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async unsetPinnedFeedItem(
    unpinRequest: RemovePinnedStateRequest,
  ): Promise<APIResponse<void>> {
    if (!unpinRequest.feedItemId) {
      return left({ message: 'Feed item is invalid or could not be found' });
    }

    try {
      await this.patch<void>({
        url: `/v1/feedItem/unpin/${unpinRequest.feedItemId}`,
        requiresToken: true,
        payload: {
          access: unpinRequest.access,
        },
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async retractFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!publishReq.id) {
      return left({ message: 'Feed Item is invalid or could not be found' });
    }
    try {
      await this.patch<void>({
        url: `/v2/feed/retract/${publishReq.id}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async archiveFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!publishReq.id) {
      return left({ message: 'Feed Item is invalid or could not be found' });
    }
    try {
      await this.post<void>({
        url: `/v1/feeditem/archive/${publishReq.id}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async retractFeedItems(
    retractReq: RetractFeedItemsRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<void>({
        url: '/v2/feeds/batch/retract',
        requiresToken: true,
        payload: retractReq,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async archiveFeedItems(
    archiveItems: ArchiveFeedItemsRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<void>({
        url: '/v2/feeds/batch/archive',
        requiresToken: true,
        payload: archiveItems,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async destroyFeedItem(
    publishReq: PublishItemRequestDTO,
  ): Promise<APIResponse<void>> {
    if (!publishReq.id) {
      return left({ message: 'Feed Item is invalid or could not be found' });
    }

    try {
      await this.delete<void>({
        url: `/v1/feeditem/destroy/${publishReq.id}`,
        requiresToken: true,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection Failed');
    }
  }

  public async fetchFeedItem(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemViewModel<FeedItemModelPropsUnion>>> {
    const { feedItemId } = viewRequest;
    try {
      const response = await this.get<ViewItemResponse>({
        url: `/v1/feedItem/view/${feedItemId}`,
        requiresToken: true,
      });

      const feedItem = FeedItemViewUtils.mapToViewModel(response.data);

      return right(
        Result.ok<FeedItemViewModel<FeedItemModelPropsUnion>>(feedItem),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connections Failed');
    }
  }

  public async fetchFeeditemViews(
    viewRequest: ViewItemFilterRequestDTO,
  ): Promise<APIResponse<LogDateCountModel[]>> {
    const { feedItemId, filter, range } = viewRequest;

    try {
      const response = await this.get<ViewItemViewsResponse>({
        url: `/v1/feedItem/analytics/${feedItemId}/views`,
        requiresToken: true,
        config: {
          params: { filter, range },
        },
      });

      const { data } = response.data;

      return right(
        Result.ok<LogDateCountModel[]>(
          data.map((item) => LogDateCountUtil.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchFeedItemViewership(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemViewershipModel>> {
    const { feedItemId } = viewRequest;
    try {
      const response = await this.get<ViewItemViewershipResponse>({
        url: `/v1/feedItem/analytics/${feedItemId}/viewership`,
        requiresToken: true,
      });

      const { data } = response.data;

      return right(
        Result.ok<FeedItemViewershipModel>(
          FeedItemViewershipUtils.mapToViewModel(data),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchFeedItemConversions(
    viewRequest: ViewItemFilterRequestDTO,
  ): Promise<APIResponse<FeedItemConversionDTO>> {
    const { feedItemId, filter, range } = viewRequest;

    try {
      const response = await this.get<ViewItemConversionsResponse>({
        url: `/v1/feedItem/analytics/${feedItemId}/conversions`,
        requiresToken: true,
        config: {
          params: { filter, range },
        },
      });

      const { data, total } = response.data;

      return right(
        Result.ok<FeedItemConversionDTO>({
          data: data.map((item) => LogDateCountUtil.mapToViewModel(item)),
          totalConversions: total,
        }),
      );
    } catch (err: any) {
      return left(err.reponse ? err.response.data : 'Connection failed');
    }
  }

  public async fetchFeedItemNotifications(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemNotificationModel[]>> {
    const { feedItemId } = viewRequest;

    try {
      const response = await this.get<ViewItemNotificationResponse>({
        url: `/v1/feedItem/analytics/${feedItemId}/notifications`,
        requiresToken: true,
      });

      const { data } = response.data;

      return right(
        Result.ok<FeedItemNotificationModel[]>(
          data.map((item) => FeedItemNotificationUtils.mapToViewModel(item)),
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchFeedItemPublishingActivity(
    viewRequest: ViewItemActivityRequestDTO,
  ): Promise<APIResponse<PaginationDataModel<FeedItemPublishingLogModel[]>>> {
    const { feedItemId, offset } = viewRequest;

    try {
      const response = await this.get<ViewItemPublishingLogResponse>({
        url: `/v2/publishingActivity/${feedItemId}/log`,
        requiresToken: true,
        config: {
          params: {
            offset,
          },
        },
      });

      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<FeedItemPublishingLogModel[]>>({
          model: data.map((item) =>
            FeedItemPublishingLogUtils.mapToViewModel(item),
          ),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchFeedItemActivity(
    viewRequest: ViewItemActivityRequestDTO,
  ): Promise<APIResponse<PaginationDataModel<FeedItemActivityLogModel[]>>> {
    const { feedItemId, offset } = viewRequest;

    try {
      const response = await this.get<ViewItemActivityLogResponse>({
        url: `/v1/feedItem/analytics/${feedItemId}/activityLog`,
        requiresToken: true,
        config: {
          params: {
            offset,
          },
        },
      });

      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<FeedItemActivityLogModel[]>>({
          model: data.map((item) =>
            FeedItemActivityLogUtils.mapToViewModel(item),
          ),
          meta: PaginationUtil.mapToViewModel(meta),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async getFeedItemAnalyticsData(
    viewRequest: ViewItemRequestDTO,
  ): Promise<APIResponse<FeedItemAnalyticsData>> {
    try {
      const views = await this.fetchFeeditemViews({
        ...viewRequest,
        filter: LogDateFilterType.WEEK,
      });

      const viewership = await this.fetchFeedItemViewership(viewRequest);

      const conversions = await this.fetchFeedItemConversions({
        ...viewRequest,
        filter: LogDateFilterType.WEEK,
      });

      const notifications = await this.fetchFeedItemNotifications(viewRequest);

      const activity = await this.fetchFeedItemActivity(viewRequest);

      const publishing = await this.fetchFeedItemPublishingActivity(
        viewRequest,
      );

      const values: FeedItemAnalyticsData = {
        views: views.isRight() ? views.value.getValue() : undefined,
        viewership: viewership.isRight()
          ? viewership.value.getValue()
          : undefined,
        conversions: conversions.isRight()
          ? conversions.value.getValue()
          : undefined,
        notifications: notifications.isRight()
          ? notifications.value.getValue()
          : undefined,
        activity: activity.isRight() ? activity.value.getValue() : undefined,
        publishing: publishing.isRight()
          ? publishing.value.getValue()
          : undefined,
      };

      for (const val of Object.values(values)) {
        if (val === undefined) {
          throw new Error('Connection Failed');
        }
      }

      return right(Result.ok<FeedItemAnalyticsData>(values));
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async deleteFeedItems(
    deleteItems: DeleteFeedItemsRequestDTO,
  ): Promise<APIResponse<void>> {
    try {
      await this.patch<void>({
        url: '/v2/feeds/batch/delete',
        requiresToken: true,
        payload: deleteItems,
      });

      return right(Result.ok<void>());
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchPublicFeedItems(
    offset?: number | undefined,
    filter?: string | undefined,
    dir?: number | undefined,
    search?: string | undefined,
  ): Promise<APIResponse<PaginationDataModel<FeaturedFeedItemModel[]>>> {
    try {
      const response = await this.get<PublicFeedItemsResponse>({
        url: '/v1/feedItems/public',
        requiresToken: true,
        config: {
          params: { offset, filter, dir, search },
        },
      });
      const { data, meta } = response.data;

      return right(
        Result.ok<PaginationDataModel<FeaturedFeedItemModel[]>>({
          meta: PaginationUtil.mapToViewModel(meta),
          model: data.map((item) => FeaturedUtil.mapToViewModel(item)),
        }),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchCommunityPinnedItem(): Promise<
    APIResponse<FeaturedFeedItemModel | null>
  > {
    try {
      const response = await this.get<CommunityPinnedItemResponse>({
        url: '/v1/feedItem/community/pinned',
        requiresToken: true,
      });

      const pinned = response.data.data;
      return right(
        Result.ok<FeaturedFeedItemModel | null>(
          pinned ? FeaturedUtil.mapToViewModel(pinned) : null,
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }

  public async fetchCommunityPinnedItems(): Promise<
    APIResponse<FeaturedFeedItemModel[]>
  > {
    try {
      const response = await this.get<CommunityPinnedItemsResponse>({
        url: '/v2/feed/community/pinned',
        requiresToken: true,
      });

      const pinned = response.data.data;

      return right(
        Result.ok<FeaturedFeedItemModel[]>(
          pinned ? pinned.map((item) => FeaturedUtil.mapToViewModel(item)) : [],
        ),
      );
    } catch (err: any) {
      return left(err.response ? err.response.data : 'Connection failed');
    }
  }
}
